// components/ui/tooltip.tsx
"use client";

import { cn } from "@nitro-ape/shared/lib/utils";
import {
  arrow as arrowMw,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset as offsetMw,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import * as React from "react";

// ============== Types (match your last <Tooltip/> props) ==============
type Side = "top" | "bottom" | "left" | "right";

export type TooltipProps = {
  content: React.ReactNode | string;
  children: React.ReactNode;
  showArrow?: boolean;
  placement?: Side;
  sideOffset?: number;
  contentClassName?: string;
  isDisabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  asChild?: boolean;
  className?: string;
};

// =================== External store (no app re-renders) ===================
type TooltipSnapshot =
  | { open: false }
  | {
      open: true;
      refEl: HTMLElement;
      content: React.ReactNode;
      side: Side;
      sideOffset: number;
      showArrow: boolean;
      className?: string;
      onClick?: (e: React.MouseEvent) => void;
    };

let state: TooltipSnapshot = { open: false };
const listeners = new Set<() => void>();

function setState(next: TooltipSnapshot) {
  state = next;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}
function getSnapshot() {
  return state;
}

// ======================= Context (imperative API) ========================
type SystemApi = {
  show: (opts: Exclude<TooltipSnapshot, { open: false }>) => void;
  hide: () => void;
};
export const SystemContext = React.createContext<SystemApi | null>(null);

// =========================== Provider (root) =============================
export function TooltipProvider({ children }: { children: React.ReactNode }) {
  // Stable functions mutate external store; provider has **no state**
  const api = React.useMemo<SystemApi>(
    () => ({
      show: (opts) => setState({ ...opts, open: true }),
      hide: () => setState({ open: false }),
    }),
    [],
  );

  return (
    <SystemContext value={api}>
      {/* Your app subtree never re-renders on hover */}
      {children}
      <TooltipLayer />
    </SystemContext>
  );
}

// ============== The single floating layer (only subscriber) ==============
function TooltipLayer() {
  const snap = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const arrowRef = React.useRef<SVGSVGElement>(null);

  const isOpen = snap.open === true;
  const side = isOpen ? snap.side : "top";
  const sideOffset = isOpen ? snap.sideOffset : 4;

  const floating = useFloating({
    open: isOpen,
    placement: side,
    whileElementsMounted: isOpen ? autoUpdate : undefined,
    elements: { reference: isOpen ? snap.refEl : null },
    middleware: [
      offsetMw(sideOffset),
      flip({ fallbackStrategy: "bestFit" }),
      shift({ padding: 6 }),
      arrowMw({ element: arrowRef }),
    ],
  });

  const role = useRole(floating.context, { role: "tooltip" });
  const dismiss = useDismiss(floating.context); // ESC closes (we also auto-hide on leave)
  const interactions = useInteractions([role, dismiss]);

  if (!isOpen) return null;

  return (
    <FloatingPortal>
      <div
        ref={floating.refs.setFloating}
        style={{
          position: floating.strategy,
          top: floating.y ?? 0,
          left: floating.x ?? 0,
          pointerEvents: "none",
        }}
        className={cn(
          // your styling
          "max-w-70 bg-background text-foreground relative z-50 rounded-lg border border-white/5 px-3 py-1.5 text-sm shadow-md",
          snap.className,
        )}
        onClick={snap.onClick}
        data-slot="tooltip-content"
        data-state="open"
        data-side={side}
        {...interactions.getFloatingProps()}
      >
        {snap.content}
        {snap.showArrow && (
          <FloatingArrow
            ref={arrowRef}
            context={floating.context}
            className="fill-background -my-px drop-shadow-[0_1px_0_var(--border)]"
            width={10}
            height={5}
          />
        )}
      </div>
    </FloatingPortal>
  );
}

// ============================ Public <Tooltip/> ===========================
function composeEventHandlers<E extends React.SyntheticEvent>(
  theirs: ((event: E) => void) | undefined,
  ours: (event: E) => void,
) {
  return (event: E) => {
    theirs?.(event);
    if (!event.defaultPrevented) {
      ours(event);
    }
  };
}

export function Tooltip({
  content,
  children,
  showArrow = false,
  placement = "top",
  sideOffset = 4,
  contentClassName,
  isDisabled = false,
  onClick,
  asChild = true,
  className,
}: TooltipProps) {
  const sys = React.use(SystemContext);

  const handleEnter = (el: HTMLElement) => {
    if (!sys) return;
    sys.show({
      open: true,
      refEl: el,
      content,
      side: placement,
      sideOffset,
      showArrow,
      className: contentClassName,
      onClick,
    });
  };

  const handleLeave = React.useCallback(() => sys?.hide(), [sys]);

  React.useEffect(() => {
    return () => {
      handleLeave();
    };
  }, [handleLeave]);

  const onMouseEnter: React.MouseEventHandler<HTMLElement> = (event) => {
    if (isDisabled) return;
    handleEnter(event.currentTarget as HTMLElement);
  };

  const onMouseLeave: React.MouseEventHandler<HTMLElement> = () => {
    if (isDisabled) return;
    handleLeave();
  };

  const onFocus: React.FocusEventHandler<HTMLElement> = (event) => {
    if (isDisabled) return;
    handleEnter(event.currentTarget as HTMLElement);
  };

  const onBlur: React.FocusEventHandler<HTMLElement> = () => {
    if (isDisabled) return;
    handleLeave();
  };

  if (asChild && React.isValidElement(children)) {
    const childProps = children.props as Record<string, unknown> & {
      className?: string;
      title?: string;
      onMouseEnter?: React.MouseEventHandler<HTMLElement>;
      onMouseLeave?: React.MouseEventHandler<HTMLElement>;
      onFocus?: React.FocusEventHandler<HTMLElement>;
      onBlur?: React.FocusEventHandler<HTMLElement>;
    };
    return React.cloneElement(children, {
      // @ts-expect-error - data-slot is not a valid prop for the child
      "data-slot": "tooltip-trigger",
      className: cn(childProps.className, className),
      onMouseEnter: composeEventHandlers(childProps.onMouseEnter, onMouseEnter),
      onMouseLeave: composeEventHandlers(childProps.onMouseLeave, onMouseLeave),
      onFocus: composeEventHandlers(childProps.onFocus, onFocus),
      onBlur: composeEventHandlers(childProps.onBlur, onBlur),
    });
  }
  return children;
}
